import { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import { useMediaQuery } from '@react-hookz/web'
import type { CellContext, ColumnDef } from '@tanstack/react-table'
import { clsx } from 'clsx'
import { observer } from 'mobx-react'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { OrdersPreviewColumnTypeEnum } from 'Modules/orders/types/OrdersPreviewColumnType.type'

import { Table } from 'Components/_theme'
import { FormatDate } from 'Components/FormatDate/FormatDate'
import { ImageWithFallback } from 'Components/ImageWithFallback/ImageWithFallback'
import { OrderStatusBadge } from 'Components/OrderStatusBadge/OrderStatusBadge'
import { ReturnPortalStatusBadge } from 'Components/ReturnPortalStatusBadge/ReturnPortalStatusBadge'
import { PassiveTagCell } from 'Components/Tagging/PassiveTagCell'
import { mapGraphqlTags } from 'Components/Tagging/tagging.utils'
import { useShowGraphqlListTags } from 'Components/Tagging/useShowGraphqlListTags'

import { useIsOrganisation } from 'Hooks'

import {
  buttonize,
  dashOrSpaceToUnderscore,
  getFullContactName,
  getOrderDisplayName,
} from 'Utilities'

import { PORTAL_BREAKPOINTS } from 'Constants/constants'
import { E2E_TABLE_DEVICE_NAME, E2E_TABLE_LINK_ORDERS } from 'Constants/e2e'

import { ImagePlaceholderTypesEnum } from 'Types/imagePlaceholders.type'

import { CaseItemType } from 'Portal/__generated__/globalTypes'
import { composePath } from 'Portal/src/utilities/composePath/composePath.util'

import type {
  EmployeeOrdersWithUrl,
  EmployeeReturnsWithUrl,
  OranisationReturnsWithUrl,
  OrdersTableProps,
  OrganisationOrdersWithUrl,
} from './OrdersTable.types'
import { getContact, getDate } from './OrdersTable.utils'

export const OrdersTable = observer((props: OrdersTableProps) => {
  const {
    orders,
    returns = [],
    isActiveEmployee,
    isLoading,
    isSearching,
    onOpenPreviewColumn,
    previewedRowIndex,
  } = props

  const { employeeId, organisationId } = useParams<{
    employeeId: string
    organisationId: string
  }>()
  const isOrganisation = useIsOrganisation()
  const intl = useIntl()
  const showTags = useShowGraphqlListTags()

  const navigate = useNavigate()
  const isTablet = useMediaQuery(`(min-width: ${PORTAL_BREAKPOINTS.LG})`)

  const handleEnterDetails = useCallback(
    (
      _event:
        | React.MouseEvent<HTMLDivElement>
        | React.KeyboardEvent<HTMLDivElement>,
      id: string,
      url: string,
      rowIndex: number,
      previewColumnType: OrdersPreviewColumnTypeEnum,
    ) => {
      if (!isTablet) {
        navigate(url)

        return
      }

      if (typeof onOpenPreviewColumn === 'function') {
        onOpenPreviewColumn(id, rowIndex, previewColumnType)
      }
    },
    [isTablet, navigate, onOpenPreviewColumn],
  )

  const renderOrdersHeader = useCallback(
    () => <FormattedMessage id="OrderListContent_header__title" />,
    [],
  )

  const renderEmployeeHeader = useCallback(
    () => <FormattedMessage id="RequestListContent_header__employee" />,
    [],
  )

  const renderDateHeader = useCallback(
    () => <FormattedMessage id="RequestListContent_header__date" />,
    [],
  )

  const renderStatusHeader = useCallback(
    () => <FormattedMessage id="RequestListContent_header__status" />,
    [],
  )

  const renderOrderCell = useCallback(
    ({
      cell,
    }: CellContext<
      | EmployeeOrdersWithUrl
      | EmployeeReturnsWithUrl
      | OranisationReturnsWithUrl
      | OrganisationOrdersWithUrl,
      unknown
    >) => {
      const item = cell.row.original

      const getOrderItemName = () => {
        if ('offerOrderItem' in item && item.offerOrderItem?.customDeviceName) {
          return item.offerOrderItem?.customDeviceName
        }

        if ('data' in item && item.data.requestedDevice) {
          return item.data.requestedDevice.name ?? ''
        }

        if (
          'data' in item &&
          item.data.requestedRentCategory?.deviceDefinition?.name
        ) {
          return item.data.requestedRentCategory?.deviceDefinition?.name
        }

        if (
          'data' in item &&
          item.data.requestedOfferOrder?.deviceDefinition?.name
        ) {
          return item.data.requestedOfferOrder?.deviceDefinition?.name
        }

        if (
          'data' in item &&
          item.data.requestedRentCategory?.deviceDefinition?.productType
        ) {
          return item.data.requestedRentCategory.deviceDefinition.productType
        }

        if (
          'data' in item &&
          dashOrSpaceToUnderscore(item.caseType) ===
            CaseItemType.new_phone_plan &&
          item.data.requestedPhonePlan?.name
        ) {
          return item.data.requestedPhonePlan.name
        }

        return ''
      }

      const getReturnItemName = () => {
        if (
          'stocklistItem' in item &&
          item.stocklistItem?.deviceDefinition?.manufacturer
        ) {
          return (
            <>
              <FormattedMessage id="Return" />
              :&nbsp;
              {`${item.stocklistItem.deviceDefinition.manufacturer} ${item.stocklistItem.deviceDefinition.codename}`}
            </>
          )
        }

        return <FormattedMessage id="Return" />
      }

      const getItemId = () => {
        if ('stocklistItem' in item && item.id) {
          return item.id
        }

        return item.id
      }

      const getReturnAndOrderItemName = () => {
        if ('stocklistItem' in item) {
          return getReturnItemName()
        }

        const displayName = getOrderDisplayName(
          item.caseType ?? '',
          getOrderItemName(),
        )

        return typeof displayName === 'string' ? (
          <FormattedMessage id={displayName} />
        ) : (
          <>
            <FormattedMessage id={displayName.type} />
            :&nbsp;{displayName.name}
          </>
        )
      }

      const getImageProps = (
        item:
          | EmployeeOrdersWithUrl
          | EmployeeReturnsWithUrl
          | OranisationReturnsWithUrl
          | OrganisationOrdersWithUrl,
      ) => {
        if ('stocklistItem' in item) {
          return {
            alt: item.stocklistItem?.deviceDefinition?.codename || '',
            placeholderType: ImagePlaceholderTypesEnum.newOrderFlow,
            src: item.stocklistItem?.deviceDefinition?.imageURL || '',
          }
        }

        return {
          alt:
            item.data.requestedRentCategory?.deviceDefinition?.codename || '',
          placeholderType: ImagePlaceholderTypesEnum.requests,
          src:
            item.data.requestedRentCategory?.deviceDefinition?.imageURL || '',
        }
      }

      const previewColumnType =
        'stocklistItem' in item
          ? OrdersPreviewColumnTypeEnum.RETURN
          : OrdersPreviewColumnTypeEnum.ORDER

      return (
        <div
          className="d-inline-flex"
          {...buttonize(
            handleEnterDetails,
            item.id,
            item.url,
            cell.row.index,
            previewColumnType,
          )}
        >
          <div className="d-flex justify-content-center align-items-center me-4 Table-device-image">
            <ImageWithFallback {...getImageProps(item)} />
          </div>

          <div className="d-flex flex-column justify-content-center">
            <div className="d-flex align-items-center">
              <strong data-e2e={E2E_TABLE_DEVICE_NAME}>
                {getReturnAndOrderItemName()}
              </strong>
            </div>

            <div className="d-flex table-text-subtitle text-muted">
              <span>#</span>

              <span className="text-ellipsis" data-orderid={item.id}>
                {getItemId()}
              </span>
            </div>
          </div>
        </div>
      )
    },
    [handleEnterDetails],
  )

  const renderExpandableContent = useCallback(
    ({
      cell,
    }: CellContext<
      | EmployeeOrdersWithUrl
      | EmployeeReturnsWithUrl
      | OranisationReturnsWithUrl
      | OrganisationOrdersWithUrl,
      unknown
    >) => {
      const item = cell.row.original
      const contact = getContact(item)
      const date = getDate(item)

      return (
        <div className="w-100 Table-details">
          {!isActiveEmployee && (
            <div className="Table-details-row">
              <span className="Table-row--expandable-title">
                {renderEmployeeHeader()}
              </span>

              <span className="text-ellipsis">
                {contact ? getFullContactName(contact, { intl }) : '-'}
              </span>
            </div>
          )}

          <div className="Table-details-row">
            <span className="Table-row--expandable-title">
              {renderDateHeader()}
            </span>

            <span>
              <FormatDate value={date} />
            </span>
          </div>

          <div className="Table-details-row">
            <span className="Table-row--expandable-title">
              {renderStatusHeader()}
            </span>

            <span>
              {'stocklistItem' in item ? (
                <ReturnPortalStatusBadge portalStatus={item.portalStatus} />
              ) : (
                <OrderStatusBadge status={item.status} />
              )}
            </span>
          </div>
        </div>
      )
    },
    [
      intl,
      isActiveEmployee,
      renderDateHeader,
      renderEmployeeHeader,
      renderStatusHeader,
    ],
  )

  const renderEmployeeCell = useCallback(
    ({
      cell,
    }: CellContext<
      | EmployeeOrdersWithUrl
      | EmployeeReturnsWithUrl
      | OranisationReturnsWithUrl
      | OrganisationOrdersWithUrl,
      unknown
    >) => {
      const item = cell.row.original
      const contact = getContact(item)
      const previewColumnType =
        'stocklistItem' in item
          ? OrdersPreviewColumnTypeEnum.RETURN
          : OrdersPreviewColumnTypeEnum.ORDER

      return (
        <div
          className="d-flex align-items-center h-100"
          {...buttonize(
            handleEnterDetails,
            item.id,
            item.url,
            cell.row.index,
            previewColumnType,
          )}
        >
          <span className="text-ellipsis">
            {contact ? getFullContactName(contact, { intl }) : '-'}
          </span>
        </div>
      )
    },
    [handleEnterDetails, intl],
  )

  const renderDateCell = useCallback(
    ({
      cell,
    }: CellContext<
      | EmployeeOrdersWithUrl
      | EmployeeReturnsWithUrl
      | OranisationReturnsWithUrl
      | OrganisationOrdersWithUrl,
      unknown
    >) => {
      const item = cell.row.original
      const date = getDate(item)
      const previewColumnType =
        'stocklistItem' in item
          ? OrdersPreviewColumnTypeEnum.RETURN
          : OrdersPreviewColumnTypeEnum.ORDER

      return (
        <div
          className="d-flex align-items-center h-100"
          {...buttonize(
            handleEnterDetails,
            item.id,
            item.url,
            cell.row.index,
            previewColumnType,
          )}
        >
          <FormatDate value={date} />
        </div>
      )
    },
    [handleEnterDetails],
  )

  const renderStatusCell = useCallback(
    ({
      cell,
    }: CellContext<
      | EmployeeOrdersWithUrl
      | EmployeeReturnsWithUrl
      | OranisationReturnsWithUrl
      | OrganisationOrdersWithUrl,
      unknown
    >) => {
      const item = cell.row.original
      const previewColumnType =
        'stocklistItem' in item
          ? OrdersPreviewColumnTypeEnum.RETURN
          : OrdersPreviewColumnTypeEnum.ORDER

      return (
        <div
          className="d-flex align-items-center h-100"
          {...buttonize(
            handleEnterDetails,
            item.id,
            item.url,
            cell.row.index,
            previewColumnType,
          )}
        >
          {'stocklistItem' in item ? (
            <ReturnPortalStatusBadge portalStatus={item.portalStatus} />
          ) : (
            <OrderStatusBadge status={item.status} />
          )}
        </div>
      )
    },
    [handleEnterDetails],
  )

  const renderTagsHeader = useCallback(() => <FormattedMessage id="Tags" />, [])

  const renderTagsCell = useCallback(
    ({
      cell,
    }: CellContext<
      | EmployeeOrdersWithUrl
      | EmployeeReturnsWithUrl
      | OranisationReturnsWithUrl
      | OrganisationOrdersWithUrl,
      unknown
    >) => {
      const item = cell.row.original

      // Returns are not taggable: keep the cell so the columns stay aligned,
      // but render no tag content at all for those rows.
      if ('stocklistItem' in item) {
        return null
      }

      return (
        <PassiveTagCell
          tags={mapGraphqlTags('tags' in item ? item.tags : null)}
        />
      )
    },
    [],
  )

  const columns = useMemo<
    ColumnDef<
      | EmployeeOrdersWithUrl
      | EmployeeReturnsWithUrl
      | OranisationReturnsWithUrl
      | OrganisationOrdersWithUrl
    >[]
  >(() => {
    const tableColumns: ColumnDef<
      | EmployeeOrdersWithUrl
      | EmployeeReturnsWithUrl
      | OranisationReturnsWithUrl
      | OrganisationOrdersWithUrl
    >[] = [
      {
        cell: renderOrderCell,
        header: renderOrdersHeader,
        id: 'order',
        meta: {
          ExpandableContent: renderExpandableContent,
        },
      },
      {
        cell: renderEmployeeCell,
        header: renderEmployeeHeader,
        id: 'employee',
      },
      {
        cell: renderDateCell,
        header: renderDateHeader,
        id: 'date',
      },
      {
        cell: renderStatusCell,
        header: renderStatusHeader,
        id: 'status',
      },
    ]

    if (showTags) {
      tableColumns.push({
        cell: renderTagsCell,
        enableSorting: false,
        header: renderTagsHeader,
        id: 'tags',
      })
    }

    return tableColumns.filter(column => {
      // don't show employee when logged in as employee - they are the same
      if (isActiveEmployee && column.id === 'employee') {
        return false
      }

      return true
    })
  }, [
    isActiveEmployee,
    renderDateCell,
    renderDateHeader,
    renderEmployeeCell,
    renderEmployeeHeader,
    renderExpandableContent,
    renderOrderCell,
    renderOrdersHeader,
    renderStatusCell,
    renderStatusHeader,
    renderTagsCell,
    renderTagsHeader,
    showTags,
  ])

  const data = useMemo((): (
    | EmployeeOrdersWithUrl
    | EmployeeReturnsWithUrl
    | OranisationReturnsWithUrl
    | OrganisationOrdersWithUrl
  )[] => {
    const parsedOrders = orders.map(order => {
      const url = composePath({
        isOrganisation,
        params: {
          employeeId,
          orderId: order.id,
          organisationId,
        },
        paths: {
          employee: GLOBAL_ROUTE_PATHS.employee.order,
          organisation: GLOBAL_ROUTE_PATHS.organisation.order,
        },
      })

      return {
        ...order,
        url,
      }
    })

    const parsedReturns = returns.map(singleReturn => {
      const url = composePath({
        isOrganisation,
        params: {
          employeeId,
          returnCaseId: singleReturn.id,
        },
        paths: {
          employee: GLOBAL_ROUTE_PATHS.employee.return,
          organisation: GLOBAL_ROUTE_PATHS.organisation.return,
        },
      })

      return {
        ...singleReturn,
        url,
      }
    })

    return [...parsedOrders, ...parsedReturns]
  }, [employeeId, isOrganisation, orders, organisationId, returns])

  return (
    <Table
      // Remounting on a gate flip stops react-table from reusing the row cells
      // of the Tags column once it is gone.
      key={showTags ? 'withTags' : 'withoutTags'}
      className={clsx(
        'Table--orders',
        isOrganisation ? 'Table--isAdmin' : 'Table--isEmployee',
      )}
      columns={columns}
      data={data}
      e2eRowId={E2E_TABLE_LINK_ORDERS}
      isLoading={isLoading}
      isSearching={isSearching}
      previewedRowIndex={previewedRowIndex}
    />
  )
})
